import { createContext, useContext } from 'react';
import type { Language } from '../../../../core/types/language';
import type { StockLocation, StoreId } from '../../../../core/types/stores';
import type { FilterList, ProductState } from '../../../queries/types';
import assert from '../../../../core/util/assert';
import type { AttributeFilters } from '../../../types';
import useLoginState from '../../../../core/hooks/useLoginState';

export interface ProductBestAvailabilityFilter {
    stockLocations: StockLocation[];
    value: number;
}

export interface ProductListQueryFilters {
    store: StoreId;
    language: Language;
    rootCategoryId: string;
    pageSize?: number;
    onSale?: boolean;
    states: ProductState[];
    categoryId?: string;
    filters: FilterList[];
    vehicleId?: string;
    bicycleId?: string;
    rootTree?: boolean;
    preorderSeasonId?: number;
    includePreorderSeasonId?: number;
    minPrice?: number;
    maxPrice?: number;
    search?: string;
    sortField: string | null;
    sortOrder: string | null;
    includeExcludedCategories?: boolean;
    bestAvailability?: ProductBestAvailabilityFilter;
    attributeFilters?: AttributeFilters;
    frontAttributes: AttributeFilters[];
    backAttributes: AttributeFilters[];
}

export interface ProductListOptionsContextValue {
    isFiltersLoading?: boolean;
    filters?: ProductListQueryFilters;
}

const ProductListOptionsContext = createContext<ProductListOptionsContextValue>(
    {
        isFiltersLoading: false,
        filters: undefined,
    }
);

export default ProductListOptionsContext;

export const useProductListOptionsContext = () => {
    const { isFiltersLoading, filters } = useContext(ProductListOptionsContext);
    const isLoggedIn = useLoginState();

    assert(
        filters,
        'Could not find filters. Please check that useProductListFilters is under ProductListContextProvider'
    );

    return {
        filters: {
            ...filters,
            bestAvailability: isLoggedIn ? filters.bestAvailability : undefined,
        },
        isFiltersLoading,
    };
};
